import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  Boxes,
  Building2,
  CheckCircle2,
  Plus,
  RefreshCw,
  Scale,
  Search,
  ShieldCheck,
  Warehouse as WarehouseIcon,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, Money, StatusPill } from "@/components/ui";
import { searchProducts } from "@/lib/endpoints";
import {
  adjustStock,
  checkIntegrity,
  createWarehouse,
  listBalances,
  listMoves,
  listWarehouses,
  rebuildBalance,
  recordMovement,
  transferStock,
  type BalancePublic,
  type IntegrityCheck,
  type MovementPublic,
  type StockAdjustment,
  type StockEntry,
  type StockTransfer,
  type WarehouseCreate,
} from "@/lib/inventory";
import { getSession } from "@/lib/session";
import { ProductModal } from "@/features/catalog/ProductModal";

export function InventoryScreen() {
  const queryClient = useQueryClient();
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  const [activeTab, setActiveTab] = useState<"existencias" | "kardex" | "almacenes">("existencias");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyNegativeFilter, setOnlyNegativeFilter] = useState(false);

  // Modals state
  const [showMovementModal, setShowMovementModal] = useState<"entrada" | "salida" | null>(null);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [auditingItem, setAuditingItem] = useState<BalancePublic | null>(null);

  // Forms state
  const [movementForm, setMovementForm] = useState<{
    warehouse_id: string;
    variant_id: string;
    variant_label: string;
    quantity: string;
    unit_cost_pesos: string;
    reason: string;
  }>({
    warehouse_id: "",
    variant_id: "",
    variant_label: "",
    quantity: "1",
    unit_cost_pesos: "",
    reason: "",
  });

  const [adjustmentForm, setAdjustmentForm] = useState<{
    warehouse_id: string;
    variant_id: string;
    variant_label: string;
    counted_quantity: string;
    unit_cost_pesos: string;
    reason: string;
  }>({
    warehouse_id: "",
    variant_id: "",
    variant_label: "",
    counted_quantity: "0",
    unit_cost_pesos: "",
    reason: "",
  });

  const [transferForm, setTransferForm] = useState<{
    from_warehouse_id: string;
    to_warehouse_id: string;
    variant_id: string;
    variant_label: string;
    quantity: string;
    reason: string;
  }>({
    from_warehouse_id: "",
    to_warehouse_id: "",
    variant_id: "",
    variant_label: "",
    quantity: "1",
    reason: "",
  });

  const [warehouseForm, setWarehouseForm] = useState<WarehouseCreate>({
    branch_id: branchId,
    code: "",
    name: "",
    address: "",
    is_default: false,
  });

  // Product search picker helper
  const [pickerSearch, setPickerSearch] = useState("");
  const { data: searchResults, isLoading: isSearchingProducts } = useQuery({
    queryKey: ["inventory", "product-picker", pickerSearch],
    queryFn: () => searchProducts(pickerSearch, branchId || undefined),
    enabled: pickerSearch.length >= 2,
  });

  // Queries
  const { data: rawWarehouses } = useQuery({
    queryKey: ["inventory", "warehouses", branchId],
    queryFn: () => listWarehouses(branchId || undefined),
  });
  const warehouses = Array.isArray(rawWarehouses) ? rawWarehouses : [];

  const effectiveWarehouseId = selectedWarehouseId === "all" ? undefined : selectedWarehouseId;

  const { data: balancesData, isLoading: isLoadingBalances } = useQuery({
    queryKey: ["inventory", "balances", effectiveWarehouseId, searchTerm, onlyNegativeFilter],
    queryFn: () =>
      listBalances({
        warehouse_id: effectiveWarehouseId,
        q: searchTerm || undefined,
        only_negative: onlyNegativeFilter || undefined,
        limit: 100,
      }),
  });

  const { data: movesData, isLoading: isLoadingMoves } = useQuery({
    queryKey: ["inventory", "moves", effectiveWarehouseId, searchTerm],
    queryFn: () =>
      listMoves({
        warehouse_id: effectiveWarehouseId,
        q: searchTerm || undefined,
        limit: 100,
      }),
    enabled: activeTab === "kardex",
  });

  const { data: integrityData, isLoading: isCheckingIntegrity, refetch: recheckIntegrity } = useQuery<IntegrityCheck | null>({
    queryKey: ["inventory", "integrity", auditingItem?.warehouse_id, auditingItem?.variant_id],
    queryFn: () => {
      if (!auditingItem) return null;
      return checkIntegrity(auditingItem.warehouse_id, auditingItem.variant_id);
    },
    enabled: Boolean(auditingItem),
  });

  // Mutations
  const movementMutation = useMutation({
    mutationFn: (payload: StockEntry) => recordMovement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setShowMovementModal(null);
      resetMovementForm();
    },
  });

  const adjustmentMutation = useMutation({
    mutationFn: (payload: StockAdjustment) => adjustStock(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setShowAdjustmentModal(false);
      resetAdjustmentForm();
    },
  });

  const transferMutation = useMutation({
    mutationFn: (payload: StockTransfer) => transferStock(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setShowTransferModal(false);
      resetTransferForm();
    },
  });

  const warehouseMutation = useMutation({
    mutationFn: (payload: WarehouseCreate) => createWarehouse(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory", "warehouses"] });
      setShowWarehouseModal(false);
      setWarehouseForm({
        branch_id: branchId,
        code: "",
        name: "",
        address: "",
        is_default: false,
      });
    },
  });

  const rebuildMutation = useMutation({
    mutationFn: ({ warehouseId, variantId }: { warehouseId: string; variantId: string }) =>
      rebuildBalance(warehouseId, variantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      recheckIntegrity();
    },
  });

  function resetMovementForm() {
    setMovementForm({
      warehouse_id: warehouses[0]?.id ?? "",
      variant_id: "",
      variant_label: "",
      quantity: "1",
      unit_cost_pesos: "",
      reason: "",
    });
    setPickerSearch("");
  }

  function resetAdjustmentForm() {
    setAdjustmentForm({
      warehouse_id: warehouses[0]?.id ?? "",
      variant_id: "",
      variant_label: "",
      counted_quantity: "0",
      unit_cost_pesos: "",
      reason: "",
    });
    setPickerSearch("");
  }

  function resetTransferForm() {
    setTransferForm({
      from_warehouse_id: warehouses[0]?.id ?? "",
      to_warehouse_id: warehouses[1]?.id ?? warehouses[0]?.id ?? "",
      variant_id: "",
      variant_label: "",
      quantity: "1",
      reason: "",
    });
    setPickerSearch("");
  }

  function handleOpenMovement(kind: "entrada" | "salida", balance?: BalancePublic) {
    setShowMovementModal(kind);
    setMovementForm({
      warehouse_id: balance?.warehouse_id || warehouses[0]?.id || "",
      variant_id: balance?.variant_id || "",
      variant_label: balance ? `${balance.product_name || "Producto"} (${balance.sku || "Sin SKU"})` : "",
      quantity: "1",
      unit_cost_pesos: balance && balance.avg_cost_cents ? (balance.avg_cost_cents / 100).toFixed(2) : "",
      reason: "",
    });
    setPickerSearch("");
  }

  function handleOpenAdjustment(balance?: BalancePublic) {
    setShowAdjustmentModal(true);
    setAdjustmentForm({
      warehouse_id: balance?.warehouse_id || warehouses[0]?.id || "",
      variant_id: balance?.variant_id || "",
      variant_label: balance ? `${balance.product_name || "Producto"} (${balance.sku || "Sin SKU"})` : "",
      counted_quantity: balance ? String(balance.quantity) : "0",
      unit_cost_pesos: balance && balance.avg_cost_cents ? (balance.avg_cost_cents / 100).toFixed(2) : "",
      reason: "",
    });
    setPickerSearch("");
  }

  // KPIs
  const balances = balancesData?.items ?? [];
  const totalItemsCount = balancesData?.total ?? 0;
  const negativeItemsCount = balances.filter((b) => b.is_negative || Number(b.quantity) < 0).length;
  const totalUnits = balances.reduce((sum, b) => sum + Math.max(0, Number(b.quantity) || 0), 0);
  const totalValuationCents = balances.reduce((sum, b) => {
    const q = Math.max(0, Number(b.quantity) || 0);
    return sum + Math.round(q * (b.avg_cost_cents || 0));
  }, 0);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-surface p-6">
      {/* Encabezado */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-lg bg-electric/15 text-electric">
              <Boxes className="size-5" />
            </span>
            <h1 className="font-display text-2xl font-bold tracking-tight text-carbon">
              Control de Inventario y Almacenes
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted">
            Monitoreo de existencias en tiempo real, kardex auditable, entradas de stock, mermas y traslados.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => setShowProductModal(true)}
            className="flex items-center gap-1.5"
          >
            <Plus className="size-4" />
            Nuevo Producto
          </Button>

          <Button
            variant="secondary"
            onClick={() => handleOpenMovement("entrada")}
            className="flex items-center gap-1.5"
          >
            <ArrowDownRight className="size-4 text-emerald-600" />
            Entrada de Stock
          </Button>

          <Button
            variant="secondary"
            onClick={() => handleOpenMovement("salida")}
            className="flex items-center gap-1.5"
          >
            <ArrowUpRight className="size-4 text-rose-600" />
            Salida / Merma
          </Button>

          <Button
            variant="secondary"
            onClick={() => handleOpenAdjustment()}
            className="flex items-center gap-1.5"
          >
            <Scale className="size-4 text-amber-600" />
            Ajuste de Conteo
          </Button>

          <Button
            variant="secondary"
            onClick={() => {
              setShowTransferModal(true);
              resetTransferForm();
            }}
            className="flex items-center gap-1.5"
          >
            <ArrowLeftRight className="size-4 text-cyan-600" />
            Traslado
          </Button>

          <Button
            onClick={() => setShowWarehouseModal(true)}
            className="flex items-center gap-1.5"
          >
            <Plus className="size-4" />
            Nuevo Almacén
          </Button>
        </div>
      </div>

      {/* Tarjetas KPI de Resumen */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Artículos en Catálogo
            </span>
            <Boxes className="size-4 text-electric" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">{totalItemsCount}</p>
          <span className="text-xs text-muted">Variantes con balance</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Unidades en Existencia
            </span>
            <CheckCircle2 className="size-4 text-emerald-600" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {totalUnits.toLocaleString("es-MX", { maximumFractionDigits: 2 })}
          </p>
          <span className="text-xs text-muted">Stock físico disponible</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Valoración Estimada
            </span>
            <Building2 className="size-4 text-cyan-600" />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            <Money cents={totalValuationCents} />
          </p>
          <span className="text-xs text-muted">A costo promedio ponderado</span>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Alertas de Stock
            </span>
            <AlertTriangle className={`size-4 ${negativeItemsCount > 0 ? "text-rose-600" : "text-muted"}`} />
          </div>
          <p className="mt-2 text-2xl font-bold text-carbon">
            {negativeItemsCount > 0 ? (
              <span className="text-danger">{negativeItemsCount} negativos</span>
            ) : (
              <span className="text-success text-lg">Al día (0 negativos)</span>
            )}
          </p>
          <span className="text-xs text-muted">Saldos en rojo por revisar</span>
        </Card>
      </div>

      {/* Controles de Filtros y Almacén */}
      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          {/* Selector de Almacén */}
          <div className="flex items-center gap-2">
            <WarehouseIcon className="size-4 text-muted" />
            <select
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
              className="rounded-msr border border-line bg-snow px-3 py-1.5 text-sm font-medium text-carbon focus:border-electric focus:outline-none"
            >
              <option value="all">Todos los almacenes ({warehouses.length})</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} - {w.name} {w.is_default ? "(Principal)" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de Pestaña */}
          <div className="flex items-center gap-1 rounded-msr border border-line bg-snow p-1">
            <button
              type="button"
              onClick={() => setActiveTab("existencias")}
              className={`rounded-msr px-3 py-1 text-xs font-semibold transition-colors ${
                activeTab === "existencias"
                  ? "bg-electric text-white"
                  : "text-muted hover:text-carbon"
              }`}
            >
              Existencias por Producto
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("kardex")}
              className={`rounded-msr px-3 py-1 text-xs font-semibold transition-colors ${
                activeTab === "kardex"
                  ? "bg-electric text-white"
                  : "text-muted hover:text-carbon"
              }`}
            >
              Kardex de Movimientos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("almacenes")}
              className={`rounded-msr px-3 py-1 text-xs font-semibold transition-colors ${
                activeTab === "almacenes"
                  ? "bg-electric text-white"
                  : "text-muted hover:text-carbon"
              }`}
            >
              Almacenes ({warehouses.length})
            </button>
          </div>
        </div>

        {/* Buscador de Producto / SKU */}
        {activeTab !== "almacenes" ? (
          <div className="flex items-center gap-3">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Buscar por producto o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow py-1.5 pl-9 pr-3 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
              />
            </div>

            {activeTab === "existencias" ? (
              <label className="flex items-center gap-1.5 text-xs font-medium text-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyNegativeFilter}
                  onChange={(e) => setOnlyNegativeFilter(e.target.checked)}
                  className="rounded border-line text-electric focus:ring-electric"
                />
                Solo negativos
              </label>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Contenido Principal por Pestaña */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        {activeTab === "existencias" ? (
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-3">Producto / Presentación</th>
                  <th className="p-3">SKU</th>
                  <th className="p-3">Almacén</th>
                  <th className="p-3 text-right">Existencias</th>
                  <th className="p-3 text-right">Costo Promedio</th>
                  <th className="p-3 text-right">Valoración</th>
                  <th className="p-3">Último Movimiento</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {isLoadingBalances ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted">
                      Cargando existencias de inventario...
                    </td>
                  </tr>
                ) : balances.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted">
                      No se encontraron registros de existencias para este almacén o filtro.
                    </td>
                  </tr>
                ) : (
                  balances.map((item) => {
                    const qty = Number(item.quantity) || 0;
                    const valuationCents = Math.round(qty * (item.avg_cost_cents || 0));

                    return (
                      <tr key={`${item.warehouse_id}-${item.variant_id}`} className="hover:bg-surface/30">
                        <td className="p-3">
                          <div className="font-semibold text-carbon">
                            {item.product_name || "Producto sin nombre"}
                          </div>
                          {item.variant_name && item.variant_name !== item.product_name ? (
                            <div className="text-xs text-muted">{item.variant_name}</div>
                          ) : null}
                        </td>

                        <td className="p-3 font-mono text-xs text-muted">
                          {item.sku || "—"}
                        </td>

                        <td className="p-3 text-xs text-carbon">
                          {item.warehouse_name || item.warehouse_id}
                        </td>

                        <td className="p-3 text-right font-medium">
                          {item.is_negative || qty < 0 ? (
                            <span className="inline-flex items-center gap-1 text-danger font-bold">
                              <AlertTriangle className="size-3.5" />
                              {qty.toLocaleString("es-MX", { maximumFractionDigits: 4 })}
                            </span>
                          ) : qty === 0 ? (
                            <span className="text-muted">0</span>
                          ) : (
                            <span className="text-carbon font-semibold">
                              {qty.toLocaleString("es-MX", { maximumFractionDigits: 4 })}
                            </span>
                          )}
                        </td>

                        <td className="p-3 text-right text-xs text-muted">
                          <Money cents={item.avg_cost_cents || 0} />
                        </td>

                        <td className="p-3 text-right text-xs font-semibold text-carbon">
                          <Money cents={valuationCents} />
                        </td>

                        <td className="p-3 text-xs text-muted">
                          {item.last_movement_at
                            ? new Date(item.last_movement_at).toLocaleString("es-MX", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })
                            : "Sin movimientos"}
                        </td>

                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenMovement("entrada", item)}
                              title="Registrar entrada rápida"
                              className="rounded p-1 text-emerald-600 hover:bg-emerald-500/10"
                            >
                              <ArrowDownRight className="size-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleOpenAdjustment(item)}
                              title="Ajuste de conteo físico"
                              className="rounded p-1 text-amber-600 hover:bg-amber-500/10"
                            >
                              <Scale className="size-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() => setAuditingItem(item)}
                              title="Verificar integridad con Kardex"
                              className="rounded p-1 text-electric hover:bg-electric/10"
                            >
                              <ShieldCheck className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : activeTab === "kardex" ? (
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-3">Fecha y Hora</th>
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Almacén</th>
                  <th className="p-3">Producto / SKU</th>
                  <th className="p-3 text-right">Cantidad</th>
                  <th className="p-3 text-right">Costo Unit.</th>
                  <th className="p-3 text-right">Costo Total</th>
                  <th className="p-3">Motivo / Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {isLoadingMoves ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted">
                      Cargando historial del kardex...
                    </td>
                  </tr>
                ) : (movesData?.items ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted">
                      No hay movimientos registrados en el kardex para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  (movesData?.items ?? []).map((m: MovementPublic) => {
                    const isIncoming = Number(m.quantity) > 0;

                    let tone: "ok" | "danger" | "warning" | "info" | "neutral" = "neutral";
                    if (m.kind === "entrada" || m.kind === "inicial") tone = "ok";
                    else if (m.kind === "salida") tone = "danger";
                    else if (m.kind.startsWith("traslado")) tone = "warning";
                    else if (m.kind === "ajuste") tone = "info";

                    return (
                      <tr key={m.id} className="hover:bg-surface/30">
                        <td className="p-3 text-xs text-muted whitespace-nowrap">
                          {new Date(m.occurred_at).toLocaleString("es-MX", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </td>

                        <td className="p-3">
                          <StatusPill tone={tone}>{m.kind}</StatusPill>
                        </td>

                        <td className="p-3 text-xs text-carbon">
                          {m.warehouse_name || m.warehouse_id}
                        </td>

                        <td className="p-3">
                          <div className="font-semibold text-carbon text-xs">
                            {m.product_name || "Producto"}
                          </div>
                          <div className="font-mono text-[11px] text-muted">{m.sku}</div>
                        </td>

                        <td className="p-3 text-right font-medium">
                          <span className={isIncoming ? "text-success font-semibold" : "text-danger font-semibold"}>
                            {isIncoming ? "+" : ""}
                            {Number(m.quantity).toLocaleString("es-MX", { maximumFractionDigits: 4 })}
                          </span>
                        </td>

                        <td className="p-3 text-right text-xs text-muted">
                          <Money cents={m.unit_cost_cents || 0} />
                        </td>

                        <td className="p-3 text-right text-xs font-semibold text-carbon">
                          <Money cents={m.total_cost_cents || 0} />
                        </td>

                        <td className="p-3 text-xs text-muted">
                          {m.reason || (m.reference_type ? `${m.reference_type} #${m.reference_id?.slice(0, 8)}` : "—")}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {warehouses.map((w) => (
              <div
                key={w.id}
                className="rounded-msr border border-line bg-snow p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="font-bold text-carbon text-base flex items-center gap-2">
                    <WarehouseIcon className="size-4 text-electric" />
                    {w.name}
                  </div>
                  {w.is_default ? (
                    <StatusPill tone="ok">Principal</StatusPill>
                  ) : (
                    <StatusPill tone="neutral">Secundario</StatusPill>
                  )}
                </div>
                <p className="font-mono text-xs text-muted font-semibold">Código: {w.code}</p>
                <p className="text-xs text-muted">
                  {w.address ? `📍 ${w.address}` : "Sin dirección específica"}
                </p>
                <div className="pt-2 border-t border-line flex justify-between items-center text-xs">
                  <span className="text-muted">Estado:</span>
                  <span className={w.is_active ? "text-success font-semibold" : "text-danger"}>
                    {w.is_active ? "Activo" : "Inactivo"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal: Entrada o Salida Manual */}
      {showMovementModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                {showMovementModal === "entrada" ? (
                  <ArrowDownRight className="size-5 text-emerald-600" />
                ) : (
                  <ArrowUpRight className="size-5 text-rose-600" />
                )}
                <h2 className="font-display text-lg font-bold text-carbon">
                  {showMovementModal === "entrada" ? "Registrar Entrada de Stock" : "Registrar Salida / Merma"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowMovementModal(null)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!movementForm.variant_id) {
                  alert("Selecciona un producto o presentación.");
                  return;
                }
                const qty = parseFloat(movementForm.quantity);
                if (qty <= 0) {
                  alert("La cantidad debe ser mayor a cero.");
                  return;
                }
                const costPesos = parseFloat(movementForm.unit_cost_pesos || "0");
                const unitCostCents = showMovementModal === "entrada" && costPesos > 0
                  ? Math.round(costPesos * 100)
                  : undefined;

                movementMutation.mutate({
                  warehouse_id: movementForm.warehouse_id || warehouses[0]?.id || "",
                  variant_id: movementForm.variant_id,
                  quantity: qty,
                  unit_cost_cents: unitCostCents,
                  kind: showMovementModal,
                  reason: movementForm.reason || undefined,
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Almacén Destino / Afectado *
                </label>
                <select
                  required
                  value={movementForm.warehouse_id}
                  onChange={(e) => setMovementForm((prev) => ({ ...prev, warehouse_id: e.target.value }))}
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                >
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} - {w.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selector de Producto */}
              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Producto o Presentación *
                </label>
                {movementForm.variant_id ? (
                  <div className="flex items-center justify-between rounded-msr border border-electric/30 bg-electric/10 p-2 text-sm text-carbon">
                    <span className="font-semibold">{movementForm.variant_label}</span>
                    <button
                      type="button"
                      onClick={() => setMovementForm((prev) => ({ ...prev, variant_id: "", variant_label: "" }))}
                      className="text-xs text-electric hover:underline"
                    >
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
                      <input
                        type="text"
                        placeholder="Escribe al menos 2 letras para buscar producto o SKU..."
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        className="w-full rounded-msr border border-line bg-snow py-2 pl-9 pr-3 text-sm text-carbon focus:border-electric focus:outline-none"
                      />
                    </div>
                    {isSearchingProducts ? (
                      <p className="text-xs text-muted">Buscando en catálogo...</p>
                    ) : (searchResults?.items ?? []).length > 0 ? (
                      <ul className="max-h-36 overflow-y-auto divide-y divide-line rounded-msr border border-line bg-snow">
                        {(searchResults?.items ?? []).map((hit) => (
                          <li key={hit.variant_id}>
                            <button
                              type="button"
                              onClick={() => {
                                setMovementForm((prev) => ({
                                  ...prev,
                                  variant_id: hit.variant_id,
                                  variant_label: `${hit.product_name} (${hit.sku})`,
                                }));
                                setPickerSearch("");
                              }}
                              className="w-full p-2 text-left text-xs hover:bg-surface flex items-center justify-between"
                            >
                              <span className="font-semibold text-carbon">{hit.product_name}</span>
                              <span className="font-mono text-muted">{hit.sku}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-carbon mb-1">
                    Cantidad *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    required
                    value={movementForm.quantity}
                    onChange={(e) => setMovementForm((prev) => ({ ...prev, quantity: e.target.value }))}
                    className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                {showMovementModal === "entrada" ? (
                  <div>
                    <label className="block text-xs font-semibold text-carbon mb-1">
                      Costo Unitario ($ Pesos)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Vacío para costo promedio"
                      value={movementForm.unit_cost_pesos}
                      onChange={(e) => setMovementForm((prev) => ({ ...prev, unit_cost_pesos: e.target.value }))}
                      className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                    />
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Motivo o Justificación
                </label>
                <input
                  type="text"
                  placeholder={showMovementModal === "entrada" ? "Ej. Compra directa, reposición inicial" : "Ej. Merma, producto dañado, muestra"}
                  value={movementForm.reason}
                  onChange={(e) => setMovementForm((prev) => ({ ...prev, reason: e.target.value }))}
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowMovementModal(null)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={movementMutation.isPending}>
                  {movementMutation.isPending ? "Guardando..." : "Confirmar Movimiento"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {/* Modal: Ajuste Físico (Conteo) */}
      {showAdjustmentModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                <Scale className="size-5 text-amber-600" />
                <h2 className="font-display text-lg font-bold text-carbon">
                  Ajuste de Inventario (Conteo Físico)
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowAdjustmentModal(false)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!adjustmentForm.variant_id) {
                  alert("Selecciona un producto o presentación.");
                  return;
                }
                const counted = parseFloat(adjustmentForm.counted_quantity);
                if (counted < 0) {
                  alert("La cantidad contada no puede ser negativa.");
                  return;
                }
                const costPesos = parseFloat(adjustmentForm.unit_cost_pesos || "0");
                const unitCostCents = costPesos > 0 ? Math.round(costPesos * 100) : undefined;

                adjustmentMutation.mutate({
                  warehouse_id: adjustmentForm.warehouse_id || warehouses[0]?.id || "",
                  variant_id: adjustmentForm.variant_id,
                  counted_quantity: counted,
                  unit_cost_cents: unitCostCents,
                  reason: adjustmentForm.reason || "Ajuste de inventario físico",
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Almacén *
                </label>
                <select
                  required
                  value={adjustmentForm.warehouse_id}
                  onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, warehouse_id: e.target.value }))}
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                >
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} - {w.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Producto o Presentación *
                </label>
                {adjustmentForm.variant_id ? (
                  <div className="flex items-center justify-between rounded-msr border border-electric/30 bg-electric/10 p-2 text-sm text-carbon">
                    <span className="font-semibold">{adjustmentForm.variant_label}</span>
                    <button
                      type="button"
                      onClick={() => setAdjustmentForm((prev) => ({ ...prev, variant_id: "", variant_label: "" }))}
                      className="text-xs text-electric hover:underline"
                    >
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
                      <input
                        type="text"
                        placeholder="Escribe al menos 2 letras para buscar producto o SKU..."
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        className="w-full rounded-msr border border-line bg-snow py-2 pl-9 pr-3 text-sm text-carbon focus:border-electric focus:outline-none"
                      />
                    </div>
                    {isSearchingProducts ? (
                      <p className="text-xs text-muted">Buscando en catálogo...</p>
                    ) : (searchResults?.items ?? []).length > 0 ? (
                      <ul className="max-h-36 overflow-y-auto divide-y divide-line rounded-msr border border-line bg-snow">
                        {(searchResults?.items ?? []).map((hit) => (
                          <li key={hit.variant_id}>
                            <button
                              type="button"
                              onClick={() => {
                                setAdjustmentForm((prev) => ({
                                  ...prev,
                                  variant_id: hit.variant_id,
                                  variant_label: `${hit.product_name} (${hit.sku})`,
                                }));
                                setPickerSearch("");
                              }}
                              className="w-full p-2 text-left text-xs hover:bg-surface flex items-center justify-between"
                            >
                              <span className="font-semibold text-carbon">{hit.product_name}</span>
                              <span className="font-mono text-muted">{hit.sku}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-carbon mb-1">
                    Cantidad Contada Real *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    required
                    value={adjustmentForm.counted_quantity}
                    onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, counted_quantity: e.target.value }))}
                    className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                  <span className="text-[11px] text-muted">
                    El sistema registrará automáticamente la diferencia en el kardex.
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-carbon mb-1">
                    Costo Unitario ($ Pesos)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Opcional"
                    value={adjustmentForm.unit_cost_pesos}
                    onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, unit_cost_pesos: e.target.value }))}
                    className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Motivo del Ajuste
                </label>
                <input
                  type="text"
                  placeholder="Ej. Conteo físico de fin de mes, corrección de descuadre"
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, reason: e.target.value }))}
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAdjustmentModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={adjustmentMutation.isPending}>
                  {adjustmentMutation.isPending ? "Ajustando..." : "Aplicar Ajuste"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {/* Modal: Traslado entre Almacenes */}
      {showTransferModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                <ArrowLeftRight className="size-5 text-cyan-600" />
                <h2 className="font-display text-lg font-bold text-carbon">
                  Traslado entre Almacenes
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!transferForm.variant_id) {
                  alert("Selecciona un producto.");
                  return;
                }
                if (transferForm.from_warehouse_id === transferForm.to_warehouse_id) {
                  alert("El almacén de origen y destino no pueden ser el mismo.");
                  return;
                }
                const qty = parseFloat(transferForm.quantity);
                if (qty <= 0) {
                  alert("La cantidad a trasladar debe ser mayor a cero.");
                  return;
                }

                transferMutation.mutate({
                  from_warehouse_id: transferForm.from_warehouse_id,
                  to_warehouse_id: transferForm.to_warehouse_id,
                  variant_id: transferForm.variant_id,
                  quantity: qty,
                  reason: transferForm.reason || "Traslado entre almacenes",
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-carbon mb-1">
                    Almacén Origen *
                  </label>
                  <select
                    required
                    value={transferForm.from_warehouse_id}
                    onChange={(e) => setTransferForm((prev) => ({ ...prev, from_warehouse_id: e.target.value }))}
                    className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.code} - {w.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-carbon mb-1">
                    Almacén Destino *
                  </label>
                  <select
                    required
                    value={transferForm.to_warehouse_id}
                    onChange={(e) => setTransferForm((prev) => ({ ...prev, to_warehouse_id: e.target.value }))}
                    className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.code} - {w.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Producto a Trasladar *
                </label>
                {transferForm.variant_id ? (
                  <div className="flex items-center justify-between rounded-msr border border-electric/30 bg-electric/10 p-2 text-sm text-carbon">
                    <span className="font-semibold">{transferForm.variant_label}</span>
                    <button
                      type="button"
                      onClick={() => setTransferForm((prev) => ({ ...prev, variant_id: "", variant_label: "" }))}
                      className="text-xs text-electric hover:underline"
                    >
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
                      <input
                        type="text"
                        placeholder="Buscar producto a trasladar..."
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        className="w-full rounded-msr border border-line bg-snow py-2 pl-9 pr-3 text-sm text-carbon focus:border-electric focus:outline-none"
                      />
                    </div>
                    {(searchResults?.items ?? []).length > 0 ? (
                      <ul className="max-h-36 overflow-y-auto divide-y divide-line rounded-msr border border-line bg-snow">
                        {(searchResults?.items ?? []).map((hit) => (
                          <li key={hit.variant_id}>
                            <button
                              type="button"
                              onClick={() => {
                                setTransferForm((prev) => ({
                                  ...prev,
                                  variant_id: hit.variant_id,
                                  variant_label: `${hit.product_name} (${hit.sku})`,
                                }));
                                setPickerSearch("");
                              }}
                              className="w-full p-2 text-left text-xs hover:bg-surface flex items-center justify-between"
                            >
                              <span className="font-semibold text-carbon">{hit.product_name}</span>
                              <span className="font-mono text-muted">{hit.sku}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Cantidad a Trasladar *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  required
                  value={transferForm.quantity}
                  onChange={(e) => setTransferForm((prev) => ({ ...prev, quantity: e.target.value }))}
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Motivo del Traslado
                </label>
                <input
                  type="text"
                  placeholder="Ej. Reabastecimiento de piso de venta, traspaso"
                  value={transferForm.reason}
                  onChange={(e) => setTransferForm((prev) => ({ ...prev, reason: e.target.value }))}
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowTransferModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={transferMutation.isPending}>
                  {transferMutation.isPending ? "Trasladando..." : "Confirmar Traslado"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {/* Modal: Nuevo Almacén */}
      {showWarehouseModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                <WarehouseIcon className="size-5 text-electric" />
                <h2 className="font-display text-lg font-bold text-carbon">
                  Registrar Nuevo Almacén
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowWarehouseModal(false)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                warehouseMutation.mutate(warehouseForm);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Código de Almacén *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej. ALM-SUR, BODEGA-2"
                  value={warehouseForm.code}
                  onChange={(e) =>
                    setWarehouseForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                  }
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm font-mono text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Nombre descriptivo *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej. Bodega de Refacciones Sur"
                  value={warehouseForm.name}
                  onChange={(e) =>
                    setWarehouseForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-carbon mb-1">
                  Ubicación o Dirección
                </label>
                <input
                  type="text"
                  placeholder="Ej. Pasillo 3, Planta Alta"
                  value={warehouseForm.address || ""}
                  onChange={(e) =>
                    setWarehouseForm((prev) => ({ ...prev, address: e.target.value }))
                  }
                  className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <label className="flex items-center gap-2 text-xs font-medium text-carbon cursor-pointer">
                <input
                  type="checkbox"
                  checked={warehouseForm.is_default}
                  onChange={(e) =>
                    setWarehouseForm((prev) => ({ ...prev, is_default: e.target.checked }))
                  }
                  className="rounded border-line text-electric focus:ring-electric"
                />
                Almacén principal por defecto de esta sucursal
              </label>

              <div className="flex justify-end gap-3 pt-3 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowWarehouseModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={warehouseMutation.isPending}>
                  {warehouseMutation.isPending ? "Guardando..." : "Crear Almacén"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {/* Modal: Auditoría de Integridad y Reconstrucción */}
      {auditingItem ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-5 text-electric" />
                <h2 className="font-display text-lg font-bold text-carbon">
                  Auditoría de Integridad de Stock
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setAuditingItem(null)}
                className="text-muted hover:text-carbon"
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="rounded-msr border border-line bg-surface/50 p-3">
                <p className="font-semibold text-carbon">{auditingItem.product_name}</p>
                <p className="font-mono text-xs text-muted">SKU: {auditingItem.sku || "—"}</p>
                <p className="text-xs text-muted">Almacén: {auditingItem.warehouse_name || auditingItem.warehouse_id}</p>
              </div>

              {isCheckingIntegrity ? (
                <div className="py-6 text-center text-muted">
                  <RefreshCw className="size-5 animate-spin mx-auto mb-2 text-electric" />
                  Calculando suma de kardex en vivo...
                </div>
              ) : integrityData ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="rounded border border-line p-2">
                      <p className="text-xs text-muted">Saldo Guardado</p>
                      <p className="text-lg font-bold text-carbon">
                        {Number(integrityData.stored_quantity).toLocaleString("es-MX")}
                      </p>
                    </div>
                    <div className="rounded border border-line p-2">
                      <p className="text-xs text-muted">Suma Kardex</p>
                      <p className="text-lg font-bold text-carbon">
                        {Number(integrityData.kardex_quantity).toLocaleString("es-MX")}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`rounded p-3 text-xs flex items-center gap-2 ${
                      integrityData.is_consistent
                        ? "bg-emerald-500/10 text-emerald-800 border border-emerald-500/20"
                        : "bg-rose-500/10 text-rose-800 border border-rose-500/20"
                    }`}
                  >
                    {integrityData.is_consistent ? (
                      <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="size-4 shrink-0 text-rose-600" />
                    )}
                    <span>{integrityData.explanation}</span>
                  </div>

                  <div className="pt-2 flex justify-between items-center text-xs text-muted">
                    <span>Costo promedio guardado: <Money cents={integrityData.stored_avg_cost_cents} /></span>
                    <span>Kardex: <Money cents={integrityData.kardex_avg_cost_cents} /></span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-between gap-3 pt-3 border-t border-line">
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  rebuildMutation.mutate({
                    warehouseId: auditingItem.warehouse_id,
                    variantId: auditingItem.variant_id,
                  })
                }
                disabled={rebuildMutation.isPending}
                className="text-xs"
              >
                {rebuildMutation.isPending ? "Reconstruyendo..." : "Reconstruir desde Kardex"}
              </Button>

              <Button
                type="button"
                variant="secondary"
                onClick={() => setAuditingItem(null)}
              >
                Cerrar
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {/* Modal para Registrar Nuevo Producto o Servicio */}
      <ProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
      />
    </div>
  );
}
